"use client";

import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { BootRecoveryPanel } from "./BootRecoveryPanel";
import { BootStep } from "./BootStep";
import { CyclingResourceLabel } from "./CyclingResourceLabel";
import { LogoMark } from "./LogoMark";
import type { WaveSnapshot } from "./types";
import type { BootRecoveryState } from "./useBootRecovery";

type BootOverlayProps = Readonly<{
  critical: WaveSnapshot;
  warm: WaveSnapshot;
  background: WaveSnapshot;
  activeWaveResources: readonly string[];
  /** Drives the fade-out when the critical wave lands and the overlay dismisses. */
  exiting?: boolean;
  /**
   * Reveal the wave stepper. Off during the startup splash (logo only) and for
   * returning users; on only for a confirmed cold start, where it slides in.
   */
  showProgress?: boolean;
  /** Watchdog state — reveals the escape hatch once the boot is deemed stuck. */
  recovery?: BootRecoveryState;
}>;

export function BootOverlay({
  critical,
  warm,
  background,
  activeWaveResources,
  exiting = false,
  showProgress = false,
  recovery,
}: BootOverlayProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Promote the <dialog> to modal on mount: adds the browser-native
  // focus trap and Esc handling. Esc is suppressed via onCancel — sync
  // can't be cancelled by the user. The dialog unmounts when the
  // consumer flips `showFullScreen` to false, which auto-closes it.
  useEffect(() => {
    const d = dialogRef.current;
    if (d && !d.open) d.showModal();
  }, []);

  return (
    <dialog
      ref={dialogRef}
      data-testid="global-boot-screen"
      aria-label={t("common.boot.title", {
        defaultValue: "Setting up your workspace",
      })}
      onCancel={(e) => e.preventDefault()}
      className="fixed inset-0 z-[100] m-0 h-full max-h-none w-full max-w-none border-0 bg-white p-0 backdrop:bg-transparent transition-opacity duration-[450ms] ease-out"
      // Appear instantly (no fade-in flash of the app behind), then dissolve
      // out over 450ms when the critical wave lands, revealing the shell.
      // A plain CSS transition paints reliably on a top-layer modal dialog.
      style={{
        // Paint containment so Dexie write storms below don't trigger
        // repaints here. The pulsing logo + bar live on the compositor.
        contain: "layout paint",
        opacity: exiting ? 0 : 1,
      }}
    >
      <div className="flex h-full items-center justify-center px-6">
        <div className="flex w-full max-w-md flex-col items-center gap-7 text-center">
          <LogoMark />

          {/* Bootstrap-only: title + stepper + status. Mounts (and slides in)
              only on a confirmed cold start, layered under the logo splash. */}
          {showProgress && (
            <div
              data-testid="boot-progress"
              className="animate-slide-in flex w-full flex-col items-center gap-7"
            >
              <div className="space-y-1.5">
                <h1 className="text-base font-medium text-gray-900">
                  {t("common.boot.title", {
                    defaultValue: "Setting up your workspace",
                  })}
                </h1>
                <p className="text-xs text-gray-500">
                  {t("common.boot.subtitle", {
                    defaultValue:
                      "We're caching your data so the app stays instant, online or off.",
                  })}
                </p>
              </div>

              <ol
                className="flex w-full flex-col gap-3"
                aria-label={t("common.boot.title", {
                  defaultValue: "Setting up your workspace",
                })}
              >
                <BootStep
                  testId="boot-step-essentials"
                  label={t("common.boot.steps.essentials", {
                    defaultValue: "Essentials",
                  })}
                  snapshot={critical}
                />
                <BootStep
                  testId="boot-step-workflows"
                  label={t("common.boot.steps.workflows", {
                    defaultValue: "Workflows",
                  })}
                  snapshot={warm}
                />
                <BootStep
                  testId="boot-step-history"
                  label={t("common.boot.steps.history", {
                    defaultValue: "History",
                  })}
                  snapshot={background}
                />
              </ol>

              <CyclingResourceLabel activeWaveResources={activeWaveResources} />

              <p className="text-[11px] text-gray-400">
                {t("common.boot.hint", {
                  defaultValue:
                    "This is a one-time setup. You'll be ready in a moment.",
                })}
              </p>
            </div>
          )}

          {recovery?.stalled && (
            <BootRecoveryPanel
              blocked={recovery.blocked}
              autoRecovering={recovery.autoRecovering}
              restart={recovery.restart}
            />
          )}
        </div>
      </div>
    </dialog>
  );
}
